/**
 * CathedralBackdrop — SFUI-001 visual mandate foundation.
 *
 * Architect directive (2026-06-23 SFUI-001):
 *  • Full-bleed space/tech background (swappable, no rebuild)
 *  • Animated circuit overlay with energy pulses
 *  • pointer-events:none — never blocks clicks
 *  • Z-stack: bg(z-0) → circuits(z-10) → content(z-20+)
 *
 * Drop this once in App.js at the root. Every page inherits the look.
 * Swap the background by setting --nx-bg-image on the html element
 * or by passing imageUrl as a prop.
 */
import React, { useMemo } from "react";

const DEFAULT_CIRCUITS = [
  // Each circuit is a list of [x,y] anchor points. Pulses travel from start→end.
  { d: "M 5,30 L 22,30 L 22,55 L 48,55 L 48,80 L 70,80",                       dur: 4.2 },
  { d: "M 95,15 L 75,15 L 75,38 L 55,38 L 55,18 L 30,18",                      dur: 5.1 },
  { d: "M 10,75 L 30,75 L 30,92 L 65,92 L 65,72 L 85,72 L 85,55",              dur: 4.7 },
  { d: "M 50,5 L 50,22 L 70,22 L 70,40 L 92,40",                               dur: 3.8 },
  { d: "M 88,90 L 70,90 L 70,68 L 45,68 L 45,42 L 20,42 L 20,18",              dur: 5.5 },
  { d: "M 2,50 L 18,50 L 18,68 L 38,68",                                       dur: 3.4 },
  { d: "M 60,98 L 60,82 L 82,82",                                              dur: 4.0 },
];

export default function CathedralBackdrop({
  imageUrl = "/static/backgrounds/nebula-default.jpg",
  showCircuits = true,
  circuitOpacity = 0.4,
  pulseColor = "#00F0FF",
}) {
  const circuits = useMemo(() => DEFAULT_CIRCUITS, []);

  return (
    <>
      {/* z-0 · BACKGROUND IMAGE / NEBULA FALLBACK */}
      <div
        data-testid="cathedral-backdrop-image"
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          backgroundImage:
            `linear-gradient(180deg, rgba(7,4,30,0.78) 0%, rgba(2,1,15,0.92) 100%), ` +
            `radial-gradient(ellipse at 22% 18%, rgba(120,60,255,0.35) 0%, transparent 55%), ` +
            `radial-gradient(ellipse at 80% 70%, rgba(212,175,55,0.18) 0%, transparent 60%), ` +
            `radial-gradient(ellipse at 50% 50%, rgba(0,240,255,0.10) 0%, transparent 70%), ` +
            `url("${imageUrl}")`,
          backgroundSize: "cover, cover, cover, cover, cover",
          backgroundPosition: "center, center, center, center, center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          pointerEvents: "none",
        }}
      />

      {/* z-0 · star field · pure CSS, no image dependency */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          backgroundImage: `
            radial-gradient(1.2px 1.2px at 12% 18%, rgba(255,255,255,0.9), transparent 50%),
            radial-gradient(1px   1px   at 27% 64%, rgba(255,255,255,0.7), transparent 50%),
            radial-gradient(1.5px 1.5px at 44% 22%, rgba(255,255,255,0.95), transparent 50%),
            radial-gradient(1px   1px   at 61% 81%, rgba(255,255,255,0.6), transparent 50%),
            radial-gradient(1.4px 1.4px at 78% 35%, rgba(255,255,255,0.85), transparent 50%),
            radial-gradient(1px   1px   at 88% 72%, rgba(255,255,255,0.7), transparent 50%),
            radial-gradient(1px   1px   at 6%  88%, rgba(255,255,255,0.6), transparent 50%),
            radial-gradient(1px   1px   at 36% 92%, rgba(255,255,255,0.5), transparent 50%),
            radial-gradient(1.2px 1.2px at 52% 8%,  rgba(255,255,255,0.85), transparent 50%),
            radial-gradient(1px   1px   at 71% 14%, rgba(255,255,255,0.7), transparent 50%),
            radial-gradient(1.3px 1.3px at 92% 48%, rgba(212,175,55,0.6),  transparent 50%),
            radial-gradient(1.1px 1.1px at 18% 50%, rgba(0,240,255,0.5),   transparent 50%)
          `,
          opacity: 0.85,
        }}
      />

      {/* z-10 · CIRCUITRY OVERLAY */}
      {showCircuits && (
        <svg
          data-testid="cathedral-backdrop-circuits"
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10,
            width: "100vw",
            height: "100vh",
            pointerEvents: "none",
            opacity: circuitOpacity,
          }}
        >
          <defs>
            <filter id="nx-pulse-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {circuits.map((c, i) => (
            <g key={i}>
              {/* faint trace */}
              <path
                d={c.d}
                stroke="rgba(255,255,255,0.15)"
                strokeWidth="0.25"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* node dots at endpoints */}
              {c.d.match(/[ML]\s*([\d.]+)\s*,\s*([\d.]+)/g)?.map((seg, j) => {
                const m = seg.match(/([\d.]+)\s*,\s*([\d.]+)/);
                if (!m) return null;
                return (
                  <circle
                    key={j}
                    cx={m[1]}
                    cy={m[2]}
                    r="0.4"
                    fill="rgba(255,255,255,0.4)"
                  />
                );
              })}
              {/* travelling pulse */}
              <circle r="0.6" fill={pulseColor} filter="url(#nx-pulse-glow)">
                <animateMotion
                  dur={`${c.dur}s`}
                  repeatCount="indefinite"
                  rotate="auto"
                  path={c.d}
                />
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.15;0.85;1"
                  dur={`${c.dur}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          ))}
        </svg>
      )}
    </>
  );
}
